import React, { useState } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useWatchContractEvent } from 'wagmi';
import { parseEther } from 'viem';
import { useNavigate } from 'react-router-dom';
import { CONTRACT_ADDRESS } from '../config/wagmi';
import RedEnvelopeABI from '../abis/RedEnvelope.json';
import './CreateEnvelope.css';

const CreateEnvelope: React.FC = () => {
  const { isConnected } = useAccount();
  const navigate = useNavigate();
  const [amount, setAmount] = useState<string>('');
  const [count, setCount] = useState<string>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // 发红包
  const { writeContract: createEnvelope, data: createHash, isPending: isCreating } = useWriteContract();
  const { isLoading: isCreatingConfirming } = useWaitForTransactionReceipt({
    hash: createHash,
  });

  // 监听红包创建事件
  // @ts-expect-error - Wagmi v3 type definitions issue
  useWatchContractEvent({
    address: CONTRACT_ADDRESS ? (CONTRACT_ADDRESS as `0x${string}`) : undefined,
    abi: RedEnvelopeABI,
    eventName: 'EnvelopeCreated',
    enabled: !!CONTRACT_ADDRESS,
    onLogs(logs: any[]) {
      const log = logs[logs.length - 1];
      if (log && log.args && log.args.envelopeId !== undefined) {
        const envelopeId = log.args.envelopeId.toString();
        setMessage({
          type: 'success',
          text: `红包创建成功！红包ID: ${envelopeId}`,
        });
        // 3秒后跳转到抢红包页面
        setTimeout(() => {
          navigate(`/claim?envelopeId=${envelopeId}`);
        }, 3000);
      }
    },
  });

  // 处理创建红包
  const handleCreateEnvelope = () => {
    if (!isConnected) {
      setMessage({ type: 'error', text: '请先连接钱包' });
      return;
    }

    if (!amount || !count) {
      setMessage({ type: 'error', text: '请输入金额和数量' });
      return;
    }

    const amountWei = parseEther(amount);
    const countNum = BigInt(count);

    if (amountWei < countNum) {
      setMessage({ type: 'error', text: '金额必须大于等于数量' });
      return;
    }

    if (!createEnvelope) {
      setMessage({ type: 'error', text: '钱包未连接或合约地址未配置' });
      return;
    }

    try {
      (createEnvelope as any)({
        address: CONTRACT_ADDRESS as `0x${string}`,
        abi: RedEnvelopeABI,
        functionName: 'createEnvelope',
        args: [countNum],
        value: amountWei,
      });
      setMessage({ type: 'info', text: '交易已提交，等待确认...' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '创建红包失败' });
    }
  };

  return (
    <div className="create-envelope-page">
      <div className="page-header">
        <h1>💰 发红包</h1>
        <p className="page-description">创建你的链上红包，让朋友们来抢吧！</p>
      </div>

      <div className="message-container">
        {message && (
          <div className={`message message-${message.type}`}>
            {message.text}
            <button onClick={() => setMessage(null)} className="close-message">×</button>
          </div>
        )}
      </div>

      <div className="create-envelope-form">
        <div className="form-group">
          <label>红包金额 (ETH):</label>
          <input
            type="number"
            step="0.001"
            min="0.001"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="例如: 0.1"
            disabled={isCreating || isCreatingConfirming}
          />
          <small>请输入要发送的 ETH 金额</small>
        </div>

        <div className="form-group">
          <label>红包个数:</label>
          <input
            type="number"
            min="1"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            placeholder="例如: 10"
            disabled={isCreating || isCreatingConfirming}
          />
          <small>红包将被分成多少个</small>
        </div>

        <div className="form-info">
          <p>💡 提示：</p>
          <ul>
            <li>金额必须大于等于红包个数</li>
            <li>每个红包至少 1 wei</li>
            <li>创建后，红包ID将自动生成</li>
          </ul>
        </div>

        <button
          onClick={handleCreateEnvelope}
          disabled={isCreating || isCreatingConfirming || !isConnected}
          className="create-button"
        >
          {isCreating || isCreatingConfirming ? '处理中...' : '发红包'}
        </button>

        {!isConnected && (
          <p className="warning-text">⚠️ 请先连接钱包</p>
        )}
      </div>
    </div>
  );
};

export default CreateEnvelope;

